import React, { ReactElement } from 'react';
import { LayoutScreenConfig, ScreenType } from '../../config/FlowTypes';

import { ScreenWrapper } from '../styles/Containers';
import { Spacer } from '../styles/Dividers';
import { NavigationProps } from './FlowNavigation';
import { PresentationLayout } from './layouts/PresentationLayout';

// Because we're not exposing the PresentationLayoutConfig directly we have a separate layout config
type TrialExplanationLayoutConfig = {
  title?: string;
  body: string[];
  image: string;
  imageAltText: string;
  primaryButtonTitle: string;
};

export type TrialExplanationScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.TrialExplanation,
  TrialExplanationLayoutConfig,
  ScreenNames
>;

type TrialExplanationScreenProps<ScreenNames extends string> = NavigationProps<
  TrialExplanationScreenConfig<ScreenNames>,
  ScreenNames
>;
// Not all screens named SharedScreenName.TrialExplanation have a screen type of ScreenType.TrialExplanation
// Screens with screenName === SharedScreenName.TrialExplanation screen defined in:
// NARRATIVE_LEARNINGS_CONTROL_CONFIG, Nerva EDUCATION_SCREENS (in a few experiment configs),
// PAIN_CONTROL_SCREENS_PRE_RESULTS, QUIT_CONFIG_4_HYPNO_TEST and a few more
// uses type: ScreenType.SingleSelect, not the type: ScreenType.TrialExplanation
// This is because they ask a 'yes/no/maybe' question that gives conditional routing.
export const TrialExplanationScreen = <ScreenNames extends string>(
  props: TrialExplanationScreenProps<ScreenNames>
): ReactElement => {
  const { navigation, layout } = props;

  return (
    <ScreenWrapper>
      <PresentationLayout
        title={layout.title}
        content={[
          ...layout.body.map((content) => ({ type: 'text' as const, content })),
          { type: 'custom', content: <Spacer space={2} /> },
          {
            type: 'image',
            src: layout.image,
            altText: layout.imageAltText,
          },
        ]}
        primaryButtonTitle={layout.primaryButtonTitle}
        onPressPrimaryButton={() => navigation.next(props)}
      />
    </ScreenWrapper>
  );
};
